import { useEffect, useSyncExternalStore } from 'react';

/**
 * What the one `?` list shows, and whether it is open.
 *
 * ONE LIST, NOT ONE PER PAGE. The dashboard, the review queue and the arrears register each had a
 * `?` of their own, and with `g` on every page a page's own list would either be a second panel or
 * a list that left out the keys that work everywhere. So a page says what its keys are — this
 * hook — and the shell's list shows them under the application's own.
 *
 * A module store rather than context: the list is mounted in the root layout, the pages are deep
 * under it, and the only thing passing between them is a short array that changes on navigation.
 */

export interface ShortcutRow {
  keys: string;
  does: string;
}

export interface PageShortcuts {
  title: string;
  rows: readonly ShortcutRow[];
}

interface State {
  open: boolean;
  pages: readonly (PageShortcuts & { id: symbol })[];
}

let state: State = { open: false, pages: [] };
const listeners = new Set<() => void>();

function set(next: State) {
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

export function useShortcutsState(): State {
  return useSyncExternalStore(subscribe, () => state);
}

export function setShortcutsOpen(open: boolean | ((was: boolean) => boolean)) {
  const next = typeof open === 'function' ? open(state.open) : open;
  if (next !== state.open) set({ ...state, open: next });
}

/**
 * Declare the keys this page adds, for as long as it is mounted.
 *
 * `rows` should be a module constant — the same array the page's key handler is tested against —
 * so the list cannot drift from what the keys do, and so this does not re-register every render.
 */
export function usePageShortcuts(title: string, rows: readonly ShortcutRow[]) {
  useEffect(() => {
    const id = Symbol(title);
    set({ ...state, pages: [...state.pages, { id, title, rows }] });
    return () => {
      set({ ...state, pages: state.pages.filter((page) => page.id !== id) });
    };
  }, [title, rows]);
}
